import { ApiError } from '@lib/api/api-response';
import { postJson } from '@lib/api/utils/post-json';
import { readApiErrorMessage } from '@lib/api/utils/read-api-error-message';
import type { DocumentUploadSource } from '@lib/observability/document-upload/types';
import type { ProcessingStageId } from '@lib/upload-processing/types/processing-stage-id';
import type { UploadProcessingProgressUpdate } from '@lib/upload-processing/types/upload-processing-progress-update';
import { isLaterProcessingStage } from '@lib/upload-processing/utils/is-later-processing-stage';
import { toast } from 'sonner';

import { waitForCollectionsProcessed } from '../hooks/use-flow-document-processing';
import type { Flow } from '../types/flow';
import type { FlowDocumentMeta } from '../types/flow-meta';
import { buildDocBuilderHref } from './build-doc-builder-href';
import {
  stageFlowDocumentFiles,
  validateFlowUploadFile,
} from './stage-flow-document-files';

interface UploadFlowDocumentParams {
  file: File;
  dealId: string;
  packageId: string;
  flowId?: string;
  uploadSource?: DocumentUploadSource;
  skipParserCacheRead?: boolean;
  skipParserCacheWrite?: boolean;
  docparserEnabled?: boolean;
  docparserVlmHopOverride?: import('@lib/docparser/types/docparser-vlm-hop-id').DocparserVlmHopId;
  /**
   * Stage the PDF and accept it ready with empty fields. Does not ask the
   * server to run field detection. Parser cache and docparser flags are
   * ignored when this is set.
   */
  skipParse?: boolean;
  onStageChange?: (stage: ProcessingStageId) => void;
  onProcessingProgress?: (update: UploadProcessingProgressUpdate) => void;
  onFirstWaveReady?: (update: UploadProcessingProgressUpdate) => void;
  isTemplate?: boolean;
  /**
   * When false (default), `onEarlyNavigate` is never invoked and uploads wait
   * for processing to finish before callers navigate (legacy UX).
   */
  earlyNavigateEnabled?: boolean;
  onEarlyNavigate?: (href: string) => void;
}

interface AcceptedFlowUploadResponse {
  flowId: string;
  collections: { collectionId: string; fileName: string }[];
}

async function getFlow(flowId: string): Promise<Flow> {
  const res = await fetch(`/api/flows/${flowId}`);
  if (!res.ok) {
    throw new ApiError(await readApiErrorMessage(res), res.status);
  }
  return (await res.json()) as Flow;
}

function isAcceptedFlowUploadResponse(
  value: Flow | AcceptedFlowUploadResponse
): value is AcceptedFlowUploadResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    'flowId' in value &&
    Array.isArray(value.collections)
  );
}

/**
 * Client-side staged upload:
 *   1) POST /api/flows/upload-document-v2/begin — mint a signed URL.
 *   2) PUT <signedUrl> — bytes go straight to Supabase Storage (bypasses the
 *      Vercel 4.5MB function-body limit).
 *   3) POST /api/flows/upload-document-v2 — server accepts (202). The default
 *      path enqueues field detection; `skipParse` lands the collection ready
 *      with empty fields and does not enqueue. The client waits for
 *      collections to finish, then refetches the flow.
 *
 * The size / PDF guard here is UX-only; the server re-checks at the bucket,
 * the /begin route, and post-download.
 */
export async function uploadFlowDocument({
  file,
  dealId,
  packageId,
  flowId,
  uploadSource,
  skipParserCacheRead,
  skipParserCacheWrite,
  docparserEnabled,
  docparserVlmHopOverride,
  skipParse,
  onStageChange,
  onProcessingProgress,
  onFirstWaveReady,
  isTemplate,
  earlyNavigateEnabled = false,
  onEarlyNavigate,
}: UploadFlowDocumentParams): Promise<Flow> {
  // Each callback only moves forward: the server's early 'parse'
  // and the replayed 'fields' can arrive in either order.
  let lastStage: ProcessingStageId | undefined;
  const reportStage = (stage: ProcessingStageId) => {
    if (!isLaterProcessingStage(stage, lastStage)) return;
    lastStage = stage;
    onStageChange?.(stage);
  };
  let lastProgressStage: ProcessingStageId | undefined;
  const reportProgress = (update: UploadProcessingProgressUpdate) => {
    if (update.stage === undefined) {
      onProcessingProgress?.(update);
      return;
    }
    if (isLaterProcessingStage(update.stage, lastProgressStage)) {
      lastProgressStage = update.stage;
      onProcessingProgress?.(update);
      return;
    }
    onProcessingProgress?.({ ...update, stage: undefined });
  };

  await validateFlowUploadFile(file);

  reportStage('upload');
  const [staged] = await stageFlowDocumentFiles({
    dealId,
    packageId,
    flowId,
    files: [file],
    // Same order as before: 'parse' fires after begin, before the PUT.
    onUrlsMinted: () => reportStage('parse'),
  });

  // Accept — server returns 202 with collection ids, then the client waits
  // for those collections to reach a terminal state before refetching the flow.
  const accepted = await postJson<AcceptedFlowUploadResponse>(
    '/api/flows/upload-document-v2',
    {
      dealId,
      packageId,
      ...(flowId && { flowId }),
      ...(uploadSource && { uploadSource }),
      ...(skipParse
        ? { skipParse: true }
        : {
            ...(skipParserCacheRead ? { skipParserCacheRead: true } : {}),
            ...(skipParserCacheWrite ? { skipParserCacheWrite: true } : {}),
            ...(docparserEnabled !== undefined ? { docparserEnabled } : {}),
            ...(docparserVlmHopOverride ? { docparserVlmHopOverride } : {}),
          }),
      files: [staged!],
    }
  );

  if (!isAcceptedFlowUploadResponse(accepted)) {
    throw new Error('Upload was accepted without a processing receipt.');
  }

  const collectionIds = accepted.collections.map(
    collection => collection.collectionId
  );
  const primaryCollectionId = collectionIds[0];
  if (primaryCollectionId) {
    // Seeds flowId and collectionId for retry targets. The
    // stage waits for the server's post-upload 'fields'.
    reportProgress({
      flowId: accepted.flowId,
      collectionId: primaryCollectionId,
    });
  }

  let navigatedEarly = false;
  let maxFieldsDetected = 0;

  const tryEarlyNavigate = (update: UploadProcessingProgressUpdate) => {
    if (!earlyNavigateEnabled || navigatedEarly) return;
    const collectionId = update.collectionId ?? primaryCollectionId;
    if (!collectionId || !update.documentId) return;
    navigatedEarly = true;
    onEarlyNavigate?.(
      buildDocBuilderHref({
        dealId,
        packageId,
        collectionId,
        isTemplate,
      })
    );
  };

  await waitForCollectionsProcessed({
    flowId: accepted.flowId,
    collectionIds,
    onStageChange: reportStage,
    onProcessingProgress: update => {
      reportProgress(update);
      if (update.fieldsDetected != null) {
        maxFieldsDetected = Math.max(maxFieldsDetected, update.fieldsDetected);
      }
      tryEarlyNavigate(update);
    },
    onFirstWaveReady: update => {
      onFirstWaveReady?.(update);
      tryEarlyNavigate(update);
    },
  });

  const flow = await getFlow(accepted.flowId);

  // The PDF landed but the parser produced no schema: the upload succeeded, so
  // warn instead of throwing. The builder shows a persistent banner; this toast
  // is the at-completion cue.
  const meta = flow.meta as FlowDocumentMeta | null | undefined;
  const detectionFailed = accepted.collections.some(
    ({ collectionId }) => meta?.collections?.[collectionId]?.detectionFailed
  );
  if (detectionFailed && maxFieldsDetected === 0) {
    toast.warning(
      "Document uploaded, but we couldn't auto-detect fields. Open it to add fields manually."
    );
  }

  return flow;
}
